import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Star } from 'lucide-react';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Brooklyn center coordinates
const BROOKLYN_CENTER = [40.6782, -73.9442];
const DEFAULT_ZOOM = 13;

// Create custom colored markers
const createColoredIcon = (status) => {
  const colors = {
    green: { bg: '#10B981', border: '#059669' },
    yellow: { bg: '#F59E0B', border: '#D97706' },
    red: { bg: '#EF4444', border: '#DC2626' },
    neutral: { bg: '#10B981', border: '#059669' }  // Default to green
  };
  
  const color = colors[status] || colors.green;  // Default to green
  
  return L.divIcon({
    className: 'custom-div-icon',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: ${color.bg};
        border: 3px solid ${color.border};
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        cursor: pointer;
      ">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3" fill="${color.border}"></circle>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Map controller component
const MapController = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom || DEFAULT_ZOOM, { duration: 0.5 });
    }
  }, [center, zoom, map]);
  
  return null;
};

// Star rating display
const StarRating = ({ rating }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-3 h-3 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
};

export const MapView = ({ 
  businesses, 
  neighborhoodCoords,
  selectedNeighborhood, 
  onBusinessClick,
  loading 
}) => {
  // Calculate map center based on selected neighborhood
  const mapCenter = useMemo(() => {
    if (selectedNeighborhood && neighborhoodCoords[selectedNeighborhood]) {
      const coords = neighborhoodCoords[selectedNeighborhood];
      return [coords.lat, coords.lng];
    }
    return BROOKLYN_CENTER;
  }, [selectedNeighborhood, neighborhoodCoords]);

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-muted">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full" data-testid="map-container">
      <MapContainer
        center={BROOKLYN_CENTER}
        zoom={DEFAULT_ZOOM}
        className="h-full w-full"
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        
        <MapController center={mapCenter} zoom={selectedNeighborhood ? 14 : DEFAULT_ZOOM} />
        
        {businesses.map((business) => (
          <Marker
            key={business.id}
            position={[business.latitude, business.longitude]}
            icon={createColoredIcon(business.status)}
            eventHandlers={{
              click: () => onBusinessClick(business)
            }}
          >
            <Popup closeButton={true}>
              <div className="p-2 min-w-[180px]">
                <h3 className="font-semibold text-sm mb-1">{business.name}</h3>
                <p className="text-xs text-gray-500 mb-2">{business.category}</p>
                <div className="flex items-center gap-2 mb-2">
                  <StarRating rating={Math.round(business.average_rating)} />
                  <span className="text-xs text-gray-500">
                    ({business.total_reviews} {business.total_reviews === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
                <button
                  onClick={() => onBusinessClick(business)}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  View details →
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};
